import { BusinessArea, CountryCode, type Language, type MeasurementSystem, PropertyType } from '@ev-tech/search-modules-api';

import { formatListingAreaRange, getListingUnit, hasPositiveRangeValue } from './utils';
import { type ListingPlotAreaDescriptor, type ListingSummary } from './types';

type PlotAreaFieldName = ListingPlotAreaDescriptor['fieldName'];

function getPlotAreaFieldName(listing: ListingSummary): PlotAreaFieldName {
  const isDevelopmentProject = 'isDevelopmentProject' in listing && listing.isDevelopmentProject;
  const isCommercialListing = 'businessArea' in listing && listing.businessArea === BusinessArea.commercial;
  const isSpanishOrPortugueseListing =
    'countryAlpha2' in listing && (listing.countryAlpha2 === CountryCode.ES || listing.countryAlpha2 === CountryCode.PT);
  const isApartment = 'propertyType' in listing && listing.propertyType === PropertyType.apartment;
  const usesTerraceSurface =
    !isDevelopmentProject &&
    !isCommercialListing &&
    isSpanishOrPortugueseListing &&
    isApartment &&
    listing.area?.terraceSurface !== undefined;

  return usesTerraceSurface ? 'area.terraceSurface' : 'area.plotSurface';
}

function getPlotAreaRange(listing: ListingSummary, fieldName: PlotAreaFieldName) {
  return fieldName === 'area.terraceSurface' ? listing.area?.terraceSurface : listing.area?.plotSurface;
}

/**
 * Selects and formats the listing area displayed in the plot-area slot.
 *
 * Spanish and Portuguese apartments use terrace surface in this slot when it is available.
 * `value` is formatted and includes the area unit. Use `resolveListingDataValue` to apply
 * the translated "from" template for development projects. It uses `listing.baseUnit` when
 * available, otherwise the supplied `measurementSystem`. Set `returnRangeValue` to `true`
 * to format distinct positive minimum and maximum values as a range; it defaults to `false`.
 */
export function getListingPlotArea(
  listing: ListingSummary,
  {
    language,
    measurementSystem,
    returnRangeValue = false,
  }: { language: Language; measurementSystem?: MeasurementSystem; returnRangeValue?: boolean },
): ListingPlotAreaDescriptor | undefined {
  const isDevelopmentProject = 'isDevelopmentProject' in listing && listing.isDevelopmentProject;
  const countryAlpha2 = 'countryAlpha2' in listing ? listing.countryAlpha2 : undefined;
  const fieldName = getPlotAreaFieldName(listing);
  const value = getPlotAreaRange(listing, fieldName);
  if (!hasPositiveRangeValue(value)) return;

  const shouldShowRoundedValue = fieldName !== 'area.plotSurface' || countryAlpha2 !== CountryCode.CH;

  return {
    fieldName,
    labelKey: fieldName === 'area.terraceSurface' ? 'search.listingData.label.terraceSurface' : 'search.listingData.label.plotSurface',
    ...(isDevelopmentProject ? { valueKey: 'search.listingData.development.label.rangePrefix' } : {}),
    value: formatListingAreaRange({
      value,
      language,
      unit: getListingUnit(listing, measurementSystem),
      shouldShowRoundedValue,
      returnRangeValue,
    }),
  };
}
